import {
  Css,
  FormPageLayout,
  StaticField,
  TagGroup,
  boundNumberField,
  boundTreeSelectField,
  useComputed,
} from "@homebound/beam";
import { ObjectConfig, required, useFormState } from "@homebound/form-state";
import { useMemo, useState } from "react";
import { createProductOfferingUrl } from "src/RouteUrls";
import { ReadyPlanVersionHeader } from "src/components/ReadyPlanVersionHeader";
import {
  LotType,
  ProductOfferingStartingConfigType,
  SaveProductOfferingStartingConfigInput,
} from "src/generated/graphqlTypes";
import { useRouteParams } from "src/hooks/useRouteParams";
import { createOptionGroups, getActiveOptions } from "src/routes/components/ProductOfferingConfigUtils";
import {
  ProductOfferingCheckoutStartingConfigPageQuery,
  useProductOfferingCheckoutStartingConfigPageQuery,
  useProductOfferingCheckoutStartingConfig_CalculatedConfigQuery,
  useProductOfferingCheckoutStartingConfig_SaveMutation,
} from "src/routes/developments/product-offerings/ProductOfferingCheckoutStartingConfigPage.generated";
import { ProductOfferingVersionParams } from "src/routes/routesDef";
import { queryResult } from "src/utils";

export function ProductOfferingCheckoutStartingConfigPage() {
  const { productOfferingId, rpavId } = useRouteParams<ProductOfferingVersionParams>();
  const query = useProductOfferingCheckoutStartingConfigPageQuery({
    variables: { productOfferingId, versionId: rpavId },
    fetchPolicy: "cache-and-network",
  });

  return queryResult(query, {
    data: ({ productOffering }) => (
      <ProductOfferingCheckoutStartingConfigPageView productOffering={productOffering} refetch={query.refetch} />
    ),
  });
}

type ProductOfferingCheckoutStartingConfigPageViewProps = {
  productOffering: ProductOfferingCheckoutStartingConfigPageQuery["productOffering"];
  refetch: () => Promise<unknown>;
};

function ProductOfferingCheckoutStartingConfigPageView(props: ProductOfferingCheckoutStartingConfigPageViewProps) {
  const { productOffering, refetch } = props;
  const { rpavId } = useRouteParams<ProductOfferingVersionParams>();
  const [saveStartingConfig] = useProductOfferingCheckoutStartingConfig_SaveMutation();

  const productOfferingUrl = createProductOfferingUrl(
    productOffering.id,
    rpavId,
    productOffering.development?.id,
    productOffering.project?.id,
  );

  // Only options on the selected RPAV's active option groups can be pinned, grouped as RPOG -> RPO
  // so the dropdown reads the same as checkout's "Included Options".
  const optionsTree = useMemo(
    () =>
      createOptionGroups(getActiveOptions(productOffering.options))
        .map((optionGroup) => ({
          id: optionGroup.id,
          name: optionGroup.name,
          children: optionGroup.readyPlanOptions.map((option) => ({
            id: option.id,
            name: option.displayName || option.name,
          })),
        }))
        .filter((optionGroup) => optionGroup.children.nonEmpty),
    [productOffering.options],
  );

  // The calculated config is read at whichever version the user picks, defaulting to the active one.
  const [calcVersionId, setCalcVersionId] = useState(productOffering.aggregateActive?.id ?? productOffering.version.id);
  const hasStartingConfig = !!productOffering.checkoutStartingConfig;
  const calcQuery = useProductOfferingCheckoutStartingConfig_CalculatedConfigQuery({
    variables: { productOfferingId: productOffering.id, versionId: calcVersionId },
    skip: !hasStartingConfig,
    // `expandedOptions` is version-dependent but hangs off an entity keyed only by id,
    // so normalizing it would make every version share (and overwrite) one cached value.
    fetchPolicy: "no-cache",
  });
  const calculatedOptions = useMemo(
    () =>
      (calcQuery.data?.productOffering.checkoutStartingConfig?.expandedOptions ?? []).sortBy(
        (option) => option.optionGroup.order,
      ),
    [calcQuery.data],
  );

  // The land/BOYL price note is only relevant when this offering's community mixes lot types,
  // i.e. it has at least one BOOL subdivision and at least one BOYL subdivision.
  const community = productOffering.project?.__typename === "Community" ? productOffering.project : undefined;
  const subdivisionLotTypes = new Set(community?.children.map((subdivision) => subdivision.lotType.code));
  const showBoylPriceNote = subdivisionLotTypes.has(LotType.Bool) && subdivisionLotTypes.has(LotType.Boyl);

  const formState = useFormState({ config: formConfig, init: { input: productOffering, map: mapToForm } });
  // Beam's SubmitButton ORs our `disabled` with its own `dirty && valid` check, and that half
  // disables with a bare boolean, so cover those cases here to always get a reason tooltip.
  const saveDisabledReason = useComputed(() => {
    if ((formState.pinnedOptionIds.value ?? []).isEmpty) return "Select at least one option";
    if (!formState.valid) return "Resolve the errors above before saving";
    if (!formState.isNewEntity && !formState.dirty) return "No changes to save";
    return false;
  }, [formState]);

  return (
    <FormPageLayout
      pageTitle={`Edit Checkout Starting Config for ${productOffering.name}`}
      breadCrumb={[{ label: productOffering.name, href: productOfferingUrl }]}
      formState={formState}
      formSections={[
        {
          title: "Details",
          rows: [
            {
              reactNodeSubtitle:
                "Pin the design options this offering's starting config is generated from, i.e. one elevation, spec level, interior scheme, and exterior scheme.",
            },
            {
              salesPriceInCents: boundNumberField({
                type: "cents",
                helperText: showBoylPriceNote
                  ? "This price does not include any land - consider this the BOYL price for this configuration. Land price is set separately on the specific Lot's Checkout Settings page."
                  : undefined,
              }),
            },
            {
              pinnedOptionIds: boundTreeSelectField({
                label: "Included Options",
                options: optionsTree,
                getOptionLabel: (option) => option.name,
                getOptionValue: (option) => option.id,
                defaultCollapsed: true,
                // Groups are only there to organize the tree; the starting config pins RPOs, so save leaves only.
                onSelect: (options) => formState.pinnedOptionIds.set(options.leaf.values),
                placeholder: "Select options",
                chipDisplay: "root",
              }),
            },
            ...(hasStartingConfig
              ? [
                  {
                    reactNodeCalculatedConfig: (
                      <div css={Css.df.fdc.gap2.$}>
                        <ReadyPlanVersionHeader
                          inline
                          label="Calculate Configuration At Version"
                          readyPlan={productOffering}
                          versionId={calcVersionId}
                          aggregateCheckoutId={productOffering.aggregateCheckout?.id}
                          onSelect={(versionId) => versionId && setCalcVersionId(versionId)}
                        />
                        <StaticField label="Calculated Configuration">
                          <div data-testid="expandedOptions">
                            {calcQuery.loading ? (
                              <span css={Css.sm.gray700.$}>Calculating…</span>
                            ) : calculatedOptions.isEmpty ? (
                              <span css={Css.sm.gray700.$}>No calculated options at this version</span>
                            ) : (
                              <TagGroup tags={calculatedOptions.map((option) => ({ text: option.displayName }))} />
                            )}
                          </div>
                        </StaticField>
                      </div>
                    ),
                  },
                ]
              : []),
          ],
        },
      ]}
      submitAction={{
        label: "Save",
        disabled: saveDisabledReason,
        onClick: async () => {
          await saveStartingConfig({ variables: { input: formState.value } });
          // Stay on the page and reload: a brand-new starting config won't attach itself to the offering in the
          // Apollo cache, and the calculated config has to be recomputed from the new pins.
          // Only refetch the calc query once a starting config exists; refetching it while skipped would
          // fire a request for one that isn't there yet.
          await Promise.all([refetch(), ...(hasStartingConfig ? [calcQuery.refetch()] : [])]);
        },
      }}
      cancelAction={{ label: "Cancel", onClick: productOfferingUrl }}
    />
  );
}

const formConfig: ObjectConfig<SaveProductOfferingStartingConfigInput> = {
  id: { type: "value" },
  type: { type: "value", rules: [required] },
  productOfferingId: { type: "value", rules: [required] },
  salesPriceInCents: { type: "value" },
  pinnedOptionIds: { type: "value" },
};

function mapToForm(
  productOffering: ProductOfferingCheckoutStartingConfigPageViewProps["productOffering"],
): SaveProductOfferingStartingConfigInput {
  const { checkoutStartingConfig } = productOffering;
  return {
    id: checkoutStartingConfig?.id,
    type: ProductOfferingStartingConfigType.Checkout,
    productOfferingId: productOffering.id,
    salesPriceInCents: checkoutStartingConfig?.salesPriceInCents,
    // Only active options are pinned; archived pins are dropped rather than silently re-saved.
    pinnedOptionIds: checkoutStartingConfig?.pinnedOptions.filter((rpo) => rpo.active).map((rpo) => rpo.id) ?? [],
  };
}
